import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAccountType, getDaysRemaining, hasFeature, getTrialExpiry } from '../context/accountUtils';
import PlanSelector from './PlanSelector';
import StickyCoreSettings from './StickyCoreSettings';
import { useConfirm } from '../components/ConfirmPopup';
import { requestHardReload } from '../services/hardReload';


const getFriendlyAndroidVoiceName = (rawName) => {
  const raw = String(rawName || '').trim();
  if (!raw) return 'Voice';

  // Android/Google TTS commonly returns IDs such as:
  // en-gb-x-gba-local, en-gb-x-gbb-local, en-us-x-iom-network.
  // Keep the raw ID as the select value, but turn it into something a user
  // can understand without making assumptions about gender.
  const parts = raw.toLowerCase().split('-').filter(Boolean);
  const languageCode = parts[0] || '';
  const regionCode =
    parts.length > 1 && /^[a-z]{2}$/i.test(parts[1]) ? parts[1].toUpperCase() : '';

  let languageName = languageCode.toUpperCase() || 'Voice';
  let regionName = regionCode;

  try {
    if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
      const languageNames = new Intl.DisplayNames(['en'], { type: 'language' });
      const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });

      languageName = languageNames.of(languageCode) || languageName;
      if (regionCode) regionName = regionNames.of(regionCode) || regionCode;
    }
  } catch {
    // Older Android WebViews may not support Intl.DisplayNames.
    const fallbackLanguages = {
      en: 'English',
      es: 'Spanish',
      fr: 'French',
      de: 'German',
      it: 'Italian',
      pt: 'Portuguese',
      nl: 'Dutch',
      pl: 'Polish',
      cy: 'Welsh',
    };
    const fallbackRegions = {
      GB: 'United Kingdom',
      US: 'United States',
      AU: 'Australia',
      CA: 'Canada',
      IE: 'Ireland',
      IN: 'India',
      ZA: 'South Africa',
      NZ: 'New Zealand',
    };

    languageName = fallbackLanguages[languageCode] || languageName;
    regionName = fallbackRegions[regionCode] || regionName;
  }

  const localeLabel = regionName
    ? `${languageName} (${regionName})`
    : languageName;

  const xIndex = parts.indexOf('x');
  const variant =
    xIndex >= 0 && parts[xIndex + 1]
      ? parts[xIndex + 1]
      : '';

  let voiceLabel = '';

  // Google's common regional IDs use values such as gba, gbb, gbc...
  // where the final character is useful as a simple stable user-facing
  // distinction. We deliberately do not label these Male/Female because
  // that information is not reliably encoded in the identifier itself.
  if (variant) {
    const regionPrefix = regionCode.toLowerCase();
    let shortVariant = variant;

    if (regionPrefix && variant.startsWith(regionPrefix)) {
      shortVariant = variant.slice(regionPrefix.length);
    }

    if (/^[a-z]$/i.test(shortVariant)) {
      voiceLabel = `Voice ${shortVariant.toUpperCase()}`;
    } else if (/^[a-z]$/i.test(variant.slice(-1))) {
      voiceLabel = `Voice ${variant.slice(-1).toUpperCase()}`;
    } else {
      voiceLabel = 'Voice';
    }
  }

  const availability = parts.includes('local')
    ? 'On-device'
    : parts.includes('network')
      ? 'Online'
      : '';

  return [
    localeLabel,
    voiceLabel,
    availability,
  ].filter(Boolean).join(' — ');
};

const getFriendlyBrowserVoiceName = (voice) => {
  if (!voice) return 'Voice';

  const name = String(voice.name || '').trim() || 'Voice';
  const lang = String(voice.lang || '').trim();

  if (!lang) return name;

  try {
    const [languageCode, regionCode] = lang.replace('_', '-').split('-');
    if (typeof Intl !== 'undefined' && Intl.DisplayNames) {
      const languageNames = new Intl.DisplayNames(['en'], { type: 'language' });
      const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
      const language = languageNames.of(languageCode.toLowerCase()) || languageCode;
      const region = regionCode
        ? regionNames.of(regionCode.toUpperCase()) || regionCode.toUpperCase()
        : '';
      return `${name} — ${region ? `${language} (${region})` : language}`;
    }
  } catch {}

  return `${name} — ${lang}`;
};

const UserProfile = ({ view = 'full' }) => {
  const { user, setUser } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [pin, setPin] = useState('');
  const [password, setPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [voice, setVoice] = useState('');
  const [availableVoices, setAvailableVoices] = useState([]);
  const [symbolFile, setSymbolFile] = useState(null);
  const plan = getAccountType();
  const daysLeft = getDaysRemaining('string');
  const [refreshKey, setRefreshKey] = useState(0);
  const { showConfirm } = useConfirm();
  const [busy, setBusy] = useState(false);
  const [profileView, setProfileView] = useState('root');

  // NEW: Feedback form state
  const [feedbackCategory, setFeedbackCategory] = useState('feedback'); // feedback | request | bug
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackEmail, setFeedbackEmail] = useState('');

  let activeSpinnerToast = null;

  const versionName = window.Android?.getAppVersion?.();     // e.g. "1.2.0"
  const versionCode = window.Android?.getAppVersionCode?.();
  let versionDisplay;
  if (versionCode === "undefined" || !versionCode) {
    versionDisplay = `2/v1.0.1`;
  }else{
    versionDisplay = `${versionCode}/v${versionName}`;
  }

  const versionBuild = localStorage.getItem('version') || '1.0.01';

  const onTestHardReload = useCallback(async () => {
    if (busy) return;
    const ok = window.confirm(
      'This will clear cached files and reload the app to fetch the latest build. Continue?'
    );
    if (!ok) return;

    try {
      setBusy(true);
      await requestHardReload();
    } finally {
      setBusy(false);
    }
  }, [busy]);

      useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const expired = params.get('upgrade');
    console.log('UserProfile mounted', window.location.pathname, window.location.search);
    if (expired === '1') {
      showToast(
        'Your account has expired. You need to upgrade your account, choose one of the available plans.',
        'info',
        true
      );
    }
  }, []);

  useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('https://jmew.xyz/aac/api/users.php', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await res.json();
        if (res.ok) {
          setName(data.user.name);
          setEmail(data.user.email);
          setPin(data.user.pin);

          // Prefill feedback email with account email
          setFeedbackEmail(data.user.email || '');
        } else {
          console.error('Failed to fetch user info:', data.error);
        }
      } catch (err) {
        console.error('Error fetching user info:', err);
      }
    };

    fetchUserInfo();
  }, []);

  useEffect(() => {
    const savedVoice = localStorage.getItem('aac-voice');
    if (savedVoice) setVoice(savedVoice);

    if (window.AndroidTTS && AndroidTTS.getAvailableVoices) {
      try {
        const voicesCSV = AndroidTTS.getAvailableVoices();
        const voiceList = voicesCSV
          .split(',')
          .map((name) => name.trim())
          .filter(Boolean)
          .map((name) => ({
            name,
            label: getFriendlyAndroidVoiceName(name),
            source: 'android',
          }));
        setAvailableVoices(voiceList);
      } catch (err) {
        console.warn('Error loading AndroidTTS voices:', err);
      }
    } else if (typeof window !== 'undefined' && window.speechSynthesis) {
      const loadVoices = () => {
        const voices = window.speechSynthesis.getVoices() || [];
        setAvailableVoices(
          voices.map((browserVoice) => ({
            ...browserVoice,
            name: browserVoice.name,
            lang: browserVoice.lang,
            label: getFriendlyBrowserVoiceName(browserVoice),
            source: 'browser',
          }))
        );
      };
      loadVoices();
      window.speechSynthesis.onvoiceschanged = loadVoices;

      return () => {
        window.speechSynthesis.onvoiceschanged = null;
      };
    }
  }, []);

  const showToast = (message, type = 'info', withSpinner = false) => {
    if (activeSpinnerToast && !withSpinner) {
      removeToast(activeSpinnerToast);
      activeSpinnerToast = null;
    }

    const toast = document.createElement('div');
    toast.innerText = message;

    const backgroundColors = {
      success: '#28a745',
      error: '#dc3545',
      info: '#007bff',
      warning: '#ffc107'
    };

    toast.style.position = 'fixed';
    toast.style.left = '50%';
    toast.style.transform = 'translateX(-50%)';
    toast.style.backgroundColor = backgroundColors[type] || '#333';
    toast.style.color = '#fff';
    toast.style.padding = '10px 20px';
    toast.style.borderRadius = '20px';
    toast.style.fontSize = '14px';
    toast.style.opacity = '0';
    toast.style.transition = 'opacity 0.5s, bottom 0.5s';
    toast.style.zIndex = '9999';
    toast.className = 'custom-toast';
    toast.style.cursor = 'pointer';
    toast.style.pointerEvents = 'auto';
    toast.style.display = 'flex';
    toast.style.alignItems = 'center';
    toast.style.gap = '10px';

    if (withSpinner) {
      const spinner = document.createElement('div');
      spinner.style.border = '3px solid #f3f3f3';
      spinner.style.borderTop = '3px solid #fff';
      spinner.style.borderRadius = '50%';
      spinner.style.width = '16px';
      spinner.style.height = '16px';
      spinner.style.animation = 'spin 1s linear infinite';
      toast.prepend(spinner);
      activeSpinnerToast = toast;
    }

    document.body.appendChild(toast);

    const allToasts = Array.from(document.querySelectorAll('.custom-toast'));
    allToasts.forEach((t, i) => {
      t.style.bottom = `${20 + i * 70}px`;
    });

    requestAnimationFrame(() => {
      toast.style.opacity = '1';
    });

    toast.onclick = () => {
      removeToast(toast);
      if (toast === activeSpinnerToast) {
        activeSpinnerToast = null;
      }
    };

    if (!withSpinner) {
      setTimeout(() => {
        removeToast(toast);
        if (toast === activeSpinnerToast) {
          activeSpinnerToast = null;
        }
      }, 2000);
    }
  };

  const removeToast = (toast) => {
    toast.style.opacity = '0';
    setTimeout(() => {
      if (document.body.contains(toast)) {
        document.body.removeChild(toast);
        const allToasts = Array.from(document.querySelectorAll('.custom-toast'));
        allToasts.forEach((t, i) => {
          t.style.bottom = `${20 + i * 70}px`;
        });
      }
    }, 500);
  };

  const handleVoiceChange = (e) => {
    setVoice(e.target.value);
  };

  const handleVoiceSubmit = () => {
    if (!hasFeature('select_voice')) {
      showToast('This feature is not available on your current plan.', 'error', false);
      return;
    }

    localStorage.setItem('aac-voice', voice);
    if (window.AndroidTTS && AndroidTTS.setVoiceByName) {
      AndroidTTS.setVoiceByName(voice);
    }
    showToast('Voice saved to device.', 'success', false);
  };

  const handleUpload = async () => {
    if (!hasFeature('upload')) {
      showToast('This feature is not available on your current plan.', 'error', false);
      return; 
    }

    if (!symbolFile) return;

    showToast('Uploading file...', 'info', true);
    const formData = new FormData();
    formData.append('image', symbolFile);

    try {
      const res = await fetch('https://jmew.xyz/aac/api/upload.php', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        body: formData,
      });

      const data = await res.json();
      if (res.ok) {
        showToast('Upload successful!', 'success', false);
      } else {
        showToast('Upload failed: ' + data.error, 'error', false);
      }
    } catch (err) {
      showToast('Upload error: ' + err.message, 'error', false);
    }
  };

  const handleInfoSubmit = async () => {
    if (!password) {
      showToast('Enter your password to update your information.', 'error', false);
      return;
    }
    if (!name || !email || !pin) {
      showToast('Please complete all fields.', 'error', false);
      return;
    }
    if (pin.length < 4) {
      showToast('PIN must be at least 4 characters long.', 'error', false);
      return;
    }
    if (!/^\d+$/.test(pin)) {
      showToast('PIN must be numeric.', 'error', false);
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      showToast('Invalid email format.', 'error', false);
      return;
    }
    if (name.length < 3) {
      showToast('Name must be at least 3 characters long.', 'error', false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://jmew.xyz/aac/api/users.php', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name, email, pin, password })
      });

      const result = await response.json();

      if (!response.ok) {
        showToast(result.error || 'Update failed.', 'error', false);
      } else {
        showToast('Information updated successfully.', 'success', false);
        localStorage.setItem('name', name);
        localStorage.setItem('pin', pin);
        setUser(prev => ({ ...prev, name, pin }));
      }
    } catch (err) {
      console.error('Update info failed:', err);
      showToast('Error updating info.', 'error', false);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!currentPassword || !newPassword || !repeatPassword) {
      showToast('Please complete all password fields.', 'error', false);
      return;
    }
    if (newPassword !== repeatPassword) {
      showToast('New passwords do not match.', 'error', false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('https://jmew.xyz/aac/api/users.php', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
          repeatPassword
        })
      });

      const result = await response.json();

      if (!response.ok) {
        showToast(result.error || 'Password update failed.', 'error', false);
      } else {
        showToast('Password changed successfully.', 'success', false);
      }
    } catch (err) {
      console.error('Password change failed:', err);
      showToast('Error changing password.', 'error', false);
    }
  };
  // NEW: Delete account
  const handleDeleteAccount = async () => {
    const confirmed = await showConfirm('Are you sure you want to delete your account? This action cannot be undone.');
    if (!confirmed) return;

    showToast('An admin will review your account for deletion.', 'info', true);

    // try {
    //   const token = localStorage.getItem('token');
    //   const response = await fetch('https://jmew.xyz/aac/api/users.php', {
    //     method: 'DELETE',
    //     headers: {
    //       'Content-Type': 'application/json',
    //       'Authorization': `Bearer ${token}`
    //     }
    //   });

    //   if (!response.ok) {
    //     const result = await response.json();
    //     showToast(result.error || 'Failed to delete account.', 'error', false);
    //   } else {
    //     showToast('Account deleted successfully.', 'success', false);
    //     localStorage.removeItem('token');
    //     navigate('/login');
    //   }
    // } catch (err) {
    //   console.error('Delete account failed:', err);
    //   showToast('Error deleting account.', 'error', false);
    // }
  };
  // NEW: Feedback submit
  const handleFeedbackSubmit = async () => {
    if (!feedbackCategory) {
      showToast('Please select a category.', 'error', false);
      return;
    }
    if (!feedbackMessage || feedbackMessage.trim().length < 10) {
      showToast('Please enter at least 10 characters.', 'error', false);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      showToast('Sending feedback...', 'info', true);

      const res = await fetch('https://jmew.xyz/aac/api/feedback.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          category: feedbackCategory,   // "feedback" | "request" | "bug"
          message: feedbackMessage,
          email: feedbackEmail || email // fallback to account email
        })
      });

      const data = await res.json();

      // remove spinner toast
      if (activeSpinnerToast) {
        removeToast(activeSpinnerToast);
        activeSpinnerToast = null;
      }

      if (res.ok) {
        setFeedbackMessage('');
        showToast('Thanks! Your message was sent.', 'success', false);
      } else {
        showToast(data.error || 'Failed to send feedback.', 'error', false);
      }
    } catch (err) {
      if (activeSpinnerToast) {
        removeToast(activeSpinnerToast);
        activeSpinnerToast = null;
      }
      showToast('Error sending feedback: ' + err.message, 'error', false);
    }
  };


  const effectiveView = view === 'full' ? profileView : view;

  const pageStyle = {
    padding: view === 'full' ? '18px' : '0',
    maxWidth: view === 'full' ? '760px' : 'none',
    margin: view === 'full' ? '0 auto' : 0,
    boxSizing: 'border-box',
  };

  const groupStyle = {
    background: '#fff',
    border: '1px solid #e2e5e9',
    borderRadius: 14,
    overflow: 'hidden',
    marginBottom: 16,
    boxShadow: '0 1px 3px rgba(0,0,0,.04)',
  };

  const groupTitleStyle = {
    margin: '0 0 7px 4px',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '.06em',
    color: '#7a818a',
    fontWeight: 700,
  };

  const inputStyle = {
    display: 'block',
    width: '100%',
    boxSizing: 'border-box',
    marginTop: 6,
    padding: '11px 12px',
    border: '1px solid #d7dbe0',
    borderRadius: 10,
    background: '#fff',
    fontSize: 15,
  };

  const primaryButton = {
    width: '100%',
    padding: '11px 14px',
    marginTop: 10,
    border: 'none',
    borderRadius: 10,
    background: '#1976d2',
    color: '#fff',
    fontWeight: 700,
    cursor: 'pointer',
    fontSize: 15,
  };

  const SettingsRow = ({ title, subtitle, onClick, danger = false }) => (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: '100%',
        border: 0,
        borderBottom: '1px solid #eceef1',
        background: '#fff',
        padding: '14px 15px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        cursor: 'pointer',
        textAlign: 'left',
      }}
    >
      <span>
        <span style={{ display: 'block', fontSize: 15, fontWeight: 650, color: danger ? '#c62828' : '#20242a' }}>
          {title}
        </span>
        {subtitle && (
          <span style={{ display: 'block', fontSize: 12, color: '#7a818a', marginTop: 3, lineHeight: 1.35 }}>
            {subtitle}
          </span>
        )}
      </span>
      <span aria-hidden="true" style={{ color: '#a0a6ad', fontSize: 22, lineHeight: 1 }}>›</span>
    </button>
  );

  const BackToSettings = () => {
    if (view !== 'full' || effectiveView === 'root') return null;
    return (
      <button
        type="button"
        onClick={() => setProfileView('root')}
        style={{
          border: 0,
          background: 'transparent',
          color: '#1976d2',
          padding: '4px 0 12px',
          fontSize: 15,
          fontWeight: 650,
          cursor: 'pointer',
        }}
      >
        ‹ Settings
      </button>
    );
  };

  const sectionHeading = (title, subtitle) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: '#1f252b' }}>{title}</div>
      {subtitle && <div style={{ fontSize: 13, color: '#727982', marginTop: 4, lineHeight: 1.45 }}>{subtitle}</div>}
    </div>
  );

  const renderRoot = () => (
    <>
      {sectionHeading(`Settings`, `${name || 'Your account'} • ${getAccountType()} — ${getDaysRemaining('string')}`)}
      <div style={groupTitleStyle}>Account</div>
      <div style={groupStyle}>
        <SettingsRow title="Payment & plans" subtitle="View or change your subscription." onClick={() => setProfileView('plans')} />
        <SettingsRow title="App settings" subtitle="Sticky Core, speech voice and custom symbols." onClick={() => setProfileView('app-settings')} />
        <SettingsRow title="Account information" subtitle="Name, email, PIN and password." onClick={() => setProfileView('account-info')} />
      </div>

      <div style={groupTitleStyle}>Support</div>
      <div style={groupStyle}>
        <SettingsRow title="Help" subtitle="Tutorial, Play Store review and feedback." onClick={() => setProfileView('help')} />
        <SettingsRow title="App information" subtitle="Version and build information." onClick={() => setProfileView('app-info')} />
      </div>

      <div style={groupTitleStyle}>Account actions</div>
      <div style={groupStyle}>
        <SettingsRow title="Delete account" danger onClick={() => setProfileView('delete-account')} />
      </div>
    </>
  );

  const renderPlans = () => (
    <>
      {sectionHeading('Payment & plans', `Current plan: ${getAccountType()} — ${getDaysRemaining('string')}`)}
      <div style={{ ...groupStyle, padding: 12 }}>
        <PlanSelector onPlanChange={() => setRefreshKey(prev => prev + 1)} />
      </div>
    </>
  );

  const renderAppSettings = () => (
    <>
      {sectionHeading('App settings', 'Communication and board preferences for this device/account.')}

      {plan === 'unlimited' && (
        <>
          <div style={groupTitleStyle}>Sticky Core</div>
          <div style={{ ...groupStyle, padding: 12 }}>
            <StickyCoreSettings />
          </div>
        </>
      )}

      <div style={groupTitleStyle}>Speech voice</div>
      <div style={{ ...groupStyle, padding: 14 }}>
        <select style={inputStyle} value={voice} onChange={handleVoiceChange}>
          {availableVoices.map((v, i) => (
            <option
              key={`${v.name}-${i}`}
              value={v.name}
              title={v.name}
            >
              {v.label || v.name}
            </option>
          ))}
        </select>
        <div style={{ marginTop: 7, fontSize: 12, color: '#727982', lineHeight: 1.4 }}>
          Voice names are simplified for readability. On-device voices work without an internet connection.
        </div>
        <button type="button" style={primaryButton} onClick={handleVoiceSubmit}>Save voice</button>
      </div>

      <div style={groupTitleStyle}>Custom symbols</div>
      <div style={{ ...groupStyle, padding: 14 }}>
        <input type="file" accept="image/*" onChange={(e) => setSymbolFile(e.target.files?.[0] || null)} />
        <button type="button" style={{ ...primaryButton, background: '#2e7d32' }} onClick={handleUpload}>Upload symbol</button>
      </div>
    </>
  );

  const renderAccountInfo = () => (
    <>
      {sectionHeading('Account information', 'Update account details or change your password.')}

      <div style={groupTitleStyle}>Personal information</div>
      <div style={{ ...groupStyle, padding: 14 }}>
        <label style={{ fontSize: 13, fontWeight: 650, color: '#4b535c' }}>Name
          <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} />
        </label>
        <label style={{ display: 'block', marginTop: 10, fontSize: 13, fontWeight: 650, color: '#4b535c' }}>Email
          <input style={inputStyle} type="email" value={email} onChange={e => setEmail(e.target.value)} />
        </label>
        <label style={{ display: 'block', marginTop: 10, fontSize: 13, fontWeight: 650, color: '#4b535c' }}>PIN
          <input style={inputStyle} inputMode="numeric" maxLength={4} value={pin} onChange={e => setPin(e.target.value)} />
        </label>
        <label style={{ display: 'block', marginTop: 10, fontSize: 13, fontWeight: 650, color: '#4b535c' }}>Password required to save
          <input style={inputStyle} type="password" value={password} onChange={e => setPassword(e.target.value)} />
        </label>
        <button type="button" style={primaryButton} onClick={handleInfoSubmit}>Save account information</button>
      </div>

      <div style={groupTitleStyle}>Change password</div>
      <div style={{ ...groupStyle, padding: 14 }}>
        <input style={inputStyle} placeholder="Current password" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} />
        <input style={inputStyle} placeholder="New password" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} />
        <input style={inputStyle} placeholder="Repeat new password" type="password" value={repeatPassword} onChange={e => setRepeatPassword(e.target.value)} />
        <button type="button" style={primaryButton} onClick={handlePasswordSubmit}>Change password</button>
      </div>
    </>
  );

  const PLAY_STORE_REVIEW_URL = 'https://play.google.com/store/apps/details?id=com.jme.iboardaac';

  const renderHelp = () => (
    <>
      {sectionHeading('Help', 'Guides, reviews and direct feedback to the iBoard team.')}

      <div style={groupTitleStyle}>Quick help</div>
      <div style={groupStyle}>
        <SettingsRow title="Open tutorial" subtitle="Run through the iBoard tutorial again." onClick={() => { window.location.href = '/aac/tutorial'; }} />
        <SettingsRow title="Leave a Play Store review" subtitle="Share your experience with other users." onClick={() => window.open(PLAY_STORE_REVIEW_URL, '_blank', 'noopener,noreferrer')} />
      </div>

      <div style={groupTitleStyle}>Feedback & suggestions</div>
      <div style={{ ...groupStyle, padding: 14 }}>
        <select style={inputStyle} value={feedbackCategory} onChange={e => setFeedbackCategory(e.target.value)}>
          <option value="feedback">General feedback</option>
          <option value="request">Feature request</option>
          <option value="bug">Bug report</option>
        </select>
        <input
          style={inputStyle}
          placeholder="Contact email (optional)"
          type="email"
          value={feedbackEmail}
          onChange={e => setFeedbackEmail(e.target.value)}
        />
        <textarea
          style={{ ...inputStyle, minHeight: 120, resize: 'vertical' }}
          placeholder="Write your message (min 10 characters)…"
          value={feedbackMessage}
          onChange={e => setFeedbackMessage(e.target.value)}
        />
        <button type="button" style={{ ...primaryButton, background: '#6f42c1' }} onClick={handleFeedbackSubmit}>Send feedback</button>
      </div>
    </>
  );

  const renderAppInfo = () => (
    <>
      {sectionHeading('App information')}
      <div style={groupStyle}>
        <div style={{ padding: '14px 15px', borderBottom: '1px solid #eceef1', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ color: '#535b64' }}>App version</span>
          <strong>{versionDisplay}</strong>
        </div>
        <div style={{ padding: '14px 15px', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ color: '#535b64' }}>Build version</span>
          <strong>{versionBuild || '1'}</strong>
        </div>
      </div>
    </>
  );

  const renderDelete = () => (
    <>
      {sectionHeading('Delete account', 'Account deletion cannot be undone.')}
      <div style={{ ...groupStyle, padding: 16 }}>
        <p style={{ marginTop: 0, color: '#5d636a', lineHeight: 1.55 }}>
          Request deletion of your iBoard account. You will be asked to confirm before anything is submitted.
        </p>
        <button
          type="button"
          style={{ ...primaryButton, background: '#c62828' }}
          onClick={handleDeleteAccount}
        >
          Delete my account
        </button>
      </div>
    </>
  );

  const renderCurrent = () => {
    switch (effectiveView) {
      case 'plans': return renderPlans();
      case 'app-settings': return renderAppSettings();
      case 'account-info': return renderAccountInfo();
      case 'help': return renderHelp();
      case 'app-info': return renderAppInfo();
      case 'delete-account': return renderDelete();
      case 'root':
      default: return renderRoot();
    }
  };

  return (
    <div style={pageStyle}>
      <BackToSettings />
      {renderCurrent()}
    </div>
  );

};

export default UserProfile;
